/**
 * Small shared UI vocabulary for the post-job step components. Pure rendering:
 * all decisions live in the machine/hook. Mirrors the visual language of the
 * previous PostJobFlow (uppercase field labels, pill inputs, 2xl cards).
 */
import { forwardRef } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { DropdownField } from '../../common/DropdownField';

export interface FlowOption<V extends string = string> {
  value: V;
  label: string;
}

/** Inline validation message (§5.5: inline, never toast-only). */
export function InlineError({ messageKey }: { messageKey: string | null }) {
  const { t } = useTranslation('employer');
  if (!messageKey) return null;
  return (
    <Text style={inlineErrorStyles.text} accessibilityRole="alert">
      {t(messageKey as never) as string}
    </Text>
  );
}

const inlineErrorStyles = StyleSheet.create({
  text: {
    color: colors.danger,
    fontSize: 12,
    lineHeight: 16,
  },
});

export function FieldLabel({ children }: { children: string }) {
  const { theme } = useTheme();
  return (
    <Text style={[fieldStyles.label, { color: theme.textTertiary }]}>
      {children}
    </Text>
  );
}

interface FlowTextFieldProps {
  label?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  errorKey?: string | null;
  multiline?: boolean;
  keyboardType?: 'default' | 'numeric';
  autoCapitalize?: 'none' | 'sentences';
  minHeight?: number;
}

export const FlowTextField = forwardRef<TextInput, FlowTextFieldProps>(
  function FlowTextField(
    {
      label,
      value,
      onChangeText,
      placeholder,
      errorKey = null,
      multiline = false,
      keyboardType = 'default',
      autoCapitalize = 'sentences',
      minHeight,
    },
    ref,
  ) {
    const { theme } = useTheme();
    return (
      <View style={fieldStyles.group}>
        {label ? <FieldLabel>{label}</FieldLabel> : null}
        <TextInput
          ref={ref}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={theme.textTertiary}
          multiline={multiline}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
          textAlignVertical={multiline ? 'top' : undefined}
          style={[
            multiline ? fieldStyles.textarea : fieldStyles.input,
            {
              borderColor: errorKey ? colors.danger : theme.border,
              backgroundColor: theme.background,
              color: theme.text,
            },
            minHeight ? { minHeight } : null,
          ]}
        />
        <InlineError messageKey={errorKey} />
      </View>
    );
  },
);

interface FlowDropdownProps<V extends string> {
  label: string;
  value: V | '';
  placeholder: string;
  options: readonly FlowOption<V>[];
  onChange: (value: V) => void;
  errorKey?: string | null;
}

export function FlowDropdown<V extends string>({
  label,
  value,
  placeholder,
  options,
  onChange,
  errorKey = null,
}: FlowDropdownProps<V>) {
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? '';
  return (
    <View style={fieldStyles.group}>
      <DropdownField
        label={label}
        value={selectedLabel}
        placeholder={placeholder}
        options={options.map((option) => option.label)}
        onSelect={(labelValue) => {
          const selected = options.find(
            (option) => option.label === labelValue,
          );
          if (selected) onChange(selected.value);
        }}
        title={label}
      />
      <InlineError messageKey={errorKey} />
    </View>
  );
}

interface FlowChipsProps<V extends string> {
  label?: string;
  value: V;
  options: readonly FlowOption<V>[];
  onChange: (value: V) => void;
  errorKey?: string | null;
}

export function FlowChips<V extends string>({
  label,
  value,
  options,
  onChange,
  errorKey = null,
}: FlowChipsProps<V>) {
  const { theme } = useTheme();
  return (
    <View style={fieldStyles.group}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <View style={fieldStyles.chipsWrap}>
        {options.map((option) => {
          const active = value === option.value;
          return (
            <Pressable
              key={option.value}
              onPress={() => onChange(option.value)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              style={[
                fieldStyles.chip,
                {
                  borderColor: active ? colors.primary : theme.border,
                  backgroundColor: active ? colors.primary : theme.background,
                },
              ]}
            >
              <Text
                style={[
                  fieldStyles.chipText,
                  { color: active ? '#ffffff' : theme.textSecondary },
                ]}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <InlineError messageKey={errorKey} />
    </View>
  );
}

export function FlowCard({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: object;
}) {
  const { theme } = useTheme();
  return (
    <View
      style={[
        fieldStyles.card,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
        style,
      ]}
    >
      {children}
    </View>
  );
}

export const fieldStyles = StyleSheet.create({
  group: {
    gap: spacing[2],
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  input: {
    minHeight: 50,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing[5],
    paddingVertical: 12,
    fontSize: 16,
  },
  textarea: {
    minHeight: 120,
    borderRadius: borderRadius['2xl'],
    borderWidth: 1,
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
    fontSize: 15,
    lineHeight: 22,
  },
  chipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  chip: {
    minHeight: 38,
    paddingHorizontal: spacing[4],
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
  },
  card: {
    borderRadius: borderRadius['2xl'],
    borderWidth: 1,
    padding: spacing[5],
    gap: spacing[4],
  },
  twoColumn: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing[4],
  },
  flexOne: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 240,
  },
  helperText: {
    fontSize: 12,
    lineHeight: 18,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[3],
    minHeight: Platform.OS === 'web' ? 36 : 44,
  },
  switchTextWrap: {
    flex: 1,
    gap: 2,
  },
  switchTitle: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '500',
  },
  switchSubtitle: {
    fontSize: 12,
    lineHeight: 17,
  },
});
